/**
 * Люди, отделы и «я» раздела.
 *
 * В прототипе это были константы. Здесь справочник заполняется с сервера
 * (`setDirectory`) до того, как раздел отрисуется, а имена экспортов
 * остались прежними — интерфейс прототипа читает их так же, как читал.
 * `ME_CLIENT` и `ME_EDITOR` объявлены через `let`: импорт ES-модуля видит
 * новое значение после заполнения.
 *
 * Падежей и пола в профилях сотрудников нет, поэтому `dat` и `gen` равны
 * имени, а фразы в интерфейсе переписаны так, чтобы склонение не требовалось.
 */

export type Dept = string;
export type PersonId = string;

export interface Person {
  id: PersonId;
  name: string;
  /** кому — в прототипе «Тимуру»; у нас имя как есть */
  dat: string;
  /** кого/чей — в прототипе «Тимура»; у нас имя как есть */
  gen: string;
  fem: boolean;
  role: string;
  dept?: Dept;
  /** отделы, где человек монтажёр */
  editorOf: Dept[];
  /** ссылка на фото или пусто — тогда аватар из инициалов */
  avatar: string;
}

function stranger(id: string): Person {
  return { id, name: "Сотрудник", dat: "Сотрудник", gen: "Сотрудник", fem: false, role: "", editorOf: [], avatar: "" };
}

const known: Record<PersonId, Person> = {};

/**
 * Незнакомый id не роняет экран: на задаче бывает человек, которого нет ни в
 * одном отделе на экране, — например, платформенный админ, поставивший задачу.
 */
export const PEOPLE: Record<PersonId, Person> = new Proxy(known, {
  get: (target, key) => (typeof key === "string" ? target[key] ?? stranger(key) : undefined),
});

export const DEPTS: Dept[] = [];
/** Название отдела → его id на сервере. */
export const DEPT_ID: Record<Dept, string> = {};
export const DEPT_NAME: Record<string, Dept> = {};
export const EDITORS: PersonId[] = [];

/** Проект отдела: ролик выходит в его паблики, туда же ставится задача. */
export interface Project {
  id: string;
  divisionId: string;
  name: string;
  groupIds: string[];
  /** Есть ли модератор. Нет — ролик принимают прямо со сдачи. */
  moderated?: boolean;
  /** Аккаунты, куда команда выкладывает ролики направления. */
  accountIds?: string[];
  /** Из них — с модерацией (08.10.2026): ролик в такой аккаунт смотрит модератор. */
  moderatedAccountIds?: string[];
  /** Имена аккаунтов от сервера: у контент-мейкера в сторе нет TikTok (09.10.2026). */
  accountNames?: Record<string, string>;
  /** Постановщики и монтажёры, состоящие в направлении сами (с 08.10.2026). Пусто — по командам. */
  makerIds?: string[];
  editorIds?: string[];
  /** Уходит ли принятый ролик в Telegram-бэкап. */
  backup?: boolean;
  /** «Президент» (23.10.2026): после «Норм» ролик сразу уходит в Instagram. */
  publishAfterModeration?: boolean;
}

/** Проекты, доступные мне. Заполняются вместе со справочником. */
export const PROJECTS: Project[] = [];

/**
 * Проходит ли ролик этого проекта модератора. Не знаем проекта (задача до
 * перехода на проекты или чужой) — считаем, что проходит: так решает и сервер.
 */
export const projectModerated = (projectId: string | null | undefined): boolean => {
  if (!projectId) return true;
  const project = PROJECTS.find((p) => p.id === projectId);
  return project ? project.moderated !== false : true;
};

/** Публикует ли направление ролик сразу после модерации — «Президент». */
export const projectPublishesAfterModeration = (projectId: string | null | undefined): boolean =>
  Boolean(projectId && PROJECTS.find((p) => p.id === projectId)?.publishAfterModeration);

/** Название направления (проекта) — «Akorda News». `null`, если не знаем. */
export const projectName = (projectId: string | null | undefined): string | null =>
  (projectId && PROJECTS.find((p) => p.id === projectId)?.name) || null;

/**
 * Направление словами там, где оно что-то говорит: у команды их несколько
 * (Алматы снимает «Алматы», Akorda News и ИИ-контент). Одно направление у
 * всех групп задачи или направление зовётся как команда — не показываем,
 * это только шум.
 */
export const directionLabel = (task: { projectId?: string | null }): string | null => {
  const project = task.projectId ? PROJECTS.find((p) => p.id === task.projectId) : null;
  if (!project) return null;
  const several = project.groupIds.some((g) => PROJECTS.filter((p) => p.groupIds.includes(g)).length > 1);
  return several ? project.name : null;
};

/**
 * Направление по умолчанию для команды — то, что названо как она («Алматы»
 * у команды Алматы). Иначе первое из списка.
 *
 * Раньше бралось просто первое, а по алфавиту латинская «Akorda News» стоит
 * раньше «Алматы»: 07.10.2026 четыре своих темы монтажёра Алматы молча ушли
 * в Akorda, мимо модератора.
 */
const LAST_PROJECT = "montage.lastProject";

/**
 * Запомнить направление, куда человек поставил тему. tanys_emes_bireu снимает
 * только Akorda, а по умолчанию стояло «Алматы» — 08.10.2026 тема ушла не в
 * то направление и попала к модератору. Теперь по умолчанию — прошлый выбор.
 */
export const rememberProject = (projectId: string) => {
  try { localStorage.setItem(LAST_PROJECT, projectId); } catch { /* приватный режим */ }
};

export const homeProject = <T extends { id: string; name: string; groupIds: string[] }>(
  projects: T[], groupId: string | null | undefined,
): T | undefined => {
  let last: string | null = null;
  try { last = localStorage.getItem(LAST_PROJECT); } catch { /* нет хранилища */ }
  const remembered = last ? projects.find((p) => p.id === last) : undefined;
  if (remembered) return remembered;
  const team = groupId ? GROUP_NAME[groupId]?.trim().toLowerCase() : undefined;
  const mine = groupId ? projects.filter((p) => p.groupIds.includes(groupId)) : [];
  return (team ? mine.find((p) => p.name.trim().toLowerCase() === team) : undefined)
    ?? mine[0] ?? projects[0];
};

/** Монтажёры, которых модератор не смотрит (bron1n — только Дархан). */
export const SKIPS_MODERATION = new Set<PersonId>();

/** Проходит ли ролик этой темы модератора: и проект с модератором, и монтажёр не из исключений. */
export const taskModerated = (
  task: { projectId?: string | null; editor?: PersonId | null },
  item?: { accountId?: string | null } | null,
): boolean => {
  if (!projectModerated(task.projectId) || (task.editor && SKIPS_MODERATION.has(task.editor))) return false;
  // Модерация — по аккаунту ролика (08.10.2026), как решает сервер (moderatorsOf):
  // аккаунт направления без галочки — мимо модератора. Без аккаунта — по направлению.
  const project = task.projectId ? PROJECTS.find((p) => p.id === task.projectId) : null;
  if (item?.accountId && project?.accountIds?.includes(item.accountId)) {
    return Boolean(project.moderatedAccountIds?.includes(item.accountId));
  }
  return true;
};

/** Проекты отдела — из них постановщик выбирает, куда ставить задачу. */
export const projectsOf = (dept: Dept) => PROJECTS.filter((p) => p.divisionId === DEPT_ID[dept]);

/** В какой группе человек работает: id → groupId. */
export const GROUP_OF: Record<PersonId, string | null> = {};

/** Названия групп (команд): groupId → «Алматы». */
export const GROUP_NAME: Record<string, string> = {};

/** Команда человека словами — «Алматы». `null`, если он не в группе. */
export const teamOf = (id: PersonId): string | null => {
  const group = GROUP_OF[id];
  return group ? GROUP_NAME[group] ?? null : null;
};

/** Направление живёт по людям (direction_members), а не по командам. */
export const byPeople = (project: Project | undefined): boolean =>
  Boolean(project && ((project.makerIds?.length ?? 0) > 0 || (project.editorIds?.length ?? 0) > 0));

/** Монтажёры направления: состоящие в нём сами, а у старых — монтажёры его команд. */
export const editorsOfProject = (projectId: string) => {
  const project = PROJECTS.find((p) => p.id === projectId);
  if (!project) return [];
  if (byPeople(project)) return EDITORS.filter((e) => project.editorIds!.includes(e));
  const ownGroup = GROUP_OF[ME_CLIENT];
  const isHead = ME_HEAD_OF.includes(project.divisionId);
  return EDITORS.filter((e) => {
    const group = GROUP_OF[e];
    return Boolean(group) && project.groupIds.includes(group!) && (isHead || group === ownGroup);
  });
};

export let ME_CLIENT: PersonId = "";
/** Как я захожу в раздел: постановщик (руководитель или контент-мейкер), монтажёр, модератор. */
export let ME_ROLE: "client" | "editor" | "moderator" | null = null;
export let ME_EDITOR: PersonId = "";
/** Отделы (id), где я руководитель: там я возвращаю на доработку и чужие задачи. */
export const ME_HEAD_OF: string[] = [];
/** Отделы (id), где я модератор. */
export const ME_MODERATOR_OF: string[] = [];

/** Монтажёры отдела — их заводят руководители на экране «Отделение». */
export const editorsOf = (dept: Dept) => EDITORS.filter((e) =>
  known[e]?.editorOf.includes(dept)
  && (ME_HEAD_OF.includes(DEPT_ID[dept]) || Boolean(GROUP_OF[e] && GROUP_OF[e] === GROUP_OF[ME_CLIENT])));

/**
 * Род глагола. В профилях его нет, поэтому фразы с глаголом по роду в разделе
 * переписаны; функция оставлена для случайной строки, которую пропустили, и
 * отдаёт мужскую форму — как было в прототипе по умолчанию.
 */
export const v = (_p: Person, m: string, _f: string) => m;

export interface DirectoryInput {
  me: {
    id: string;
    name: string;
    avatarUrl: string | null;
    role: "client" | "editor" | "moderator" | null;
    headOf?: string[];
    moderatorOf?: string[];
  };
  divisions: { id: string; name: string }[];
  projects?: { id: string; divisionId: string; name: string; groupIds: string[]; moderated?: boolean; accountIds?: string[]; moderatedAccountIds?: string[]; accountNames?: Record<string, string>; makerIds?: string[]; editorIds?: string[]; backup?: boolean; publishAfterModeration?: boolean }[];
  groups?: { id: string; name: string }[];
  people: {
    id: string; name: string; avatarUrl: string | null; divisionId: string;
    groupId?: string | null; kind: "editor" | "client" | "moderator"; skipModeration?: boolean;
  }[];
}

export function setDirectory(input: DirectoryInput) {
  for (const k of Object.keys(known)) delete known[k];
  DEPTS.splice(0, DEPTS.length);
  EDITORS.splice(0, EDITORS.length);
  for (const k of Object.keys(DEPT_ID)) delete DEPT_ID[k];
  for (const k of Object.keys(DEPT_NAME)) delete DEPT_NAME[k];
  for (const k of Object.keys(GROUP_OF)) delete GROUP_OF[k];
  for (const k of Object.keys(GROUP_NAME)) delete GROUP_NAME[k];
  SKIPS_MODERATION.clear();
  for (const p of input.people) if (p.skipModeration) SKIPS_MODERATION.add(p.id);
  for (const g of input.groups ?? []) GROUP_NAME[g.id] = g.name;
  PROJECTS.splice(0, PROJECTS.length, ...(input.projects ?? []));

  for (const d of input.divisions) {
    DEPTS.push(d.name);
    DEPT_ID[d.name] = d.id;
    DEPT_NAME[d.id] = d.name;
  }

  const upsert = (id: string, name: string, avatarUrl: string | null) =>
    (known[id] ??= { id, name, dat: name, gen: name, fem: false, role: "", editorOf: [], avatar: avatarUrl ?? "" });

  for (const p of input.people) {
    const dept = DEPT_NAME[p.divisionId];
    const person = upsert(p.id, p.name, p.avatarUrl);
    if (p.kind === "editor" && dept) {
      if (!person.editorOf.includes(dept)) person.editorOf.push(dept);
      if (!EDITORS.includes(p.id)) EDITORS.push(p.id);
    }
    person.dept ??= dept;
    GROUP_OF[p.id] = p.groupId ?? null;
    const label = p.kind === "editor" ? "Монтажёр" : p.kind === "moderator" ? "Модератор" : "Контент-мейкер";
    if (!person.role) person.role = dept ? `${label} · ${dept}` : label;
  }

  const me = upsert(input.me.id, input.me.name, input.me.avatarUrl);
  if (!me.role) me.role = input.me.role === "editor" ? "Монтажёр" : input.me.role === "moderator" ? "Модератор" : "Контент-мейкер";
  ME_CLIENT = input.me.id;
  ME_ROLE = input.me.role;
  ME_EDITOR = input.me.id;
  ME_HEAD_OF.splice(0, ME_HEAD_OF.length, ...(input.me.headOf ?? []));
  ME_MODERATOR_OF.splice(0, ME_MODERATOR_OF.length, ...(input.me.moderatorOf ?? []));
}

/** Люди, которых нет в отделах экрана, но они встречаются на задачах. */
export function rememberPeople(list: { id: string; name: string; avatarUrl: string | null }[]) {
  for (const p of list) {
    known[p.id] ??= { id: p.id, name: p.name, dat: p.name, gen: p.name, fem: false, role: "", editorOf: [], avatar: p.avatarUrl ?? "" };
  }
}

/**
 * Я — постановщик этой темы: принимаю по ней видео, назначаю и переношу.
 *
 * Тот же расчёт, что `isClientOf` на сервере: руководитель отдела — любая
 * тема, контент-мейкер — темы своей группы (группа исполнителя, у свободной —
 * адресат, без адресата — проект моей группы). Решает всё равно сервер; здесь
 * — чтобы не показывать кнопку, на которую он ответит отказом.
 */
export function isMyStaging(task: { divisionId: string; editor: PersonId; groupId: string | null; projectId: string | null }): boolean {
  if (ME_HEAD_OF.includes(task.divisionId)) return true;
  if (ME_ROLE !== "client") return false;
  // Постановщик направления (direction_members) — его ролики принимаю я.
  const project = PROJECTS.find((p) => p.id === task.projectId);
  if (project?.makerIds?.includes(ME_CLIENT)) return true;
  const mine = GROUP_OF[ME_CLIENT];
  if (!mine) return false;
  const group = (task.editor && GROUP_OF[task.editor]) || task.groupId;
  if (group) return group === mine;
  return PROJECTS.some((p) => p.id === task.projectId && p.groupIds.includes(mine));
}
